import ClearIcon from '@mui/icons-material/Clear';
import { Autocomplete, SxProps, TextField, Theme, Typography } from '@mui/material';
import DOMPurify from 'dompurify';
import { useState } from 'react';
import { Control, Controller, FieldValues, Path, RegisterOptions } from 'react-hook-form';
import { getOptions } from '../../../api/asyncTypeaheadClient';
import { SelectOption } from '../../../models/form';

interface HeiAsyncTypeaheadProps<TForm extends FieldValues> {
    control: Control<TForm>;
    name: Path<TForm>;
    label: string;
    placeholder?: string;
    sx?: SxProps<Theme>;
    rules?: Omit<
        RegisterOptions<TForm>,
        'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
    >;
    endpoint: string;
    title?: string;
    description?: string;
    disabled?: boolean;
    requireInput?: boolean;
}

const HeiAsyncTypeahead = <TForm extends FieldValues>({
    control,
    name,
    label,
    placeholder,
    sx,
    rules,
    endpoint,
    title,
    description,
    disabled,
}: HeiAsyncTypeaheadProps<TForm>): JSX.Element => {
    const required = rules?.required;

    const [options, setOptions] = useState([] as SelectOption[]);
    const [isLoading, setIsLoading] = useState(false);

    const handleInputChange = async (value: string, reason: string) => {
        if (reason === 'input') {
            if (value.length < 3) return;
            setIsLoading(true);
            const fetchedOptions = await getOptions(endpoint, value);
            setOptions(fetchedOptions);
            setIsLoading(false);
        }
    };

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ fieldState, field: { ref, onChange, value: formValue, ...field } }) => {
                const error = fieldState.error;
                const errorMessage = error?.message;

                return (
                    <>
                        {title && (
                            <Typography variant='body2' sx={{ color: '#202020', mb: 1, mt: 1 }}>
                                {title}
                            </Typography>
                        )}
                        {description && (
                            <Typography variant='body2' sx={{ mb: 3 }}>
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(description),
                                    }}
                                />
                            </Typography>
                        )}
                        <Autocomplete
                            disabled={disabled}
                            sx={{ mb: 3, width: '70% !important' }}
                            loading={isLoading}
                            autoComplete
                            value={formValue}
                            onChange={(_, data) => onChange((data as SelectOption)?.value ?? '')}
                            onInputChange={(event, newInputValue, reason) =>
                                handleInputChange(newInputValue, reason)
                            }
                            clearIcon={<ClearIcon fontSize='small' />}
                            options={options}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    {...field}
                                    fullWidth
                                    inputRef={ref}
                                    required={!!required}
                                    InputLabelProps={{ shrink: true }}
                                    sx={sx}
                                    label={label}
                                    variant='outlined'
                                    placeholder={placeholder}
                                    error={error !== undefined}
                                    helperText={errorMessage}
                                />
                            )}
                        />
                    </>
                );
            }}
        />
    );
};

export default HeiAsyncTypeahead;
